import React, { useMemo } from 'react';
import { Modal, Box, Typography, Button } from '@mui/material';
import { useTheme } from '@emotion/react';
import { useMediaQuery } from '@mui/material';
import { useTiersGameContext } from '../context/TiersGameContext';
import { useUserAndGameContext } from '../../../contexts/UserAndGameContext';

const TiersUserSelectionModal = ({ open, onClose, entry, interval }) => {
    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '85%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflow: 'auto',
        bgcolor: 'background.paper', // White background
        borderRadius: '8px', // 8px border radius as requested
        boxShadow: 24,
        p: 2,
    };

    const {
        intervalInfo,
    } = useUserAndGameContext();

    const {
        players,
        firstInterval,
        theCurrentInterval,
        hasTheCurrentIntervalsDeadlinePassed,
    } = useTiersGameContext();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const intervalName = (intervalNum) => {
        const intervalData = intervalInfo?.info?.find(data => data.interval === intervalNum);
        return intervalData?.shortLabel;
    };

    const formatScore = (score) => {
        if (score === undefined || score === null) {
            return '(N/A)';
        } else if (score === 0) {
            return '(E)';
        } else if (score > 0) {
            return `(+${score})`;
        } else {
            return `(${score})`;
        }
    };

    const findScore = (entry, interval) => {
        if (interval === 'Overall') {
            return formatScore(entry?.overallScore);
        } else {
            // in Interval Data find the object where interval === interval
            const intervalData = entry?.intervalData?.find(data => data.interval === interval);
            return formatScore(intervalData?.intervalScore);
        }
    }

    const findRank = (entry, interval) => {
        if (interval === 'Overall') {
            return entry?.rank;
        } else {
            // in Interval Data find the object where interval === interval
            const intervalData = entry?.intervalData?.find(data => data.interval === interval);
            return intervalData?.rank;
        }
    }
    
    const getPlayerName = (playerId) => {
        if (!players || !playerId) return "No Pick";
        
        const player = players.find(p => p.PlayerID === Number(playerId));
        return player ? `${player.FirstName} ${player.LastName}` : "No Pick";
    };

    // Determine the two worst tiers for a specific interval
    const getWorstTiersForInterval = (intervalData) => {
        if (!intervalData || !intervalData.players) return [];
        
        // Convert to array for sorting with their scores
        const picksArray = intervalData.players.map(player => ({
            tier: player.tier,
            overallScore: player.overallScore
        }));
        
        // Filter out picks that don't have scores
        const picksWithScores = picksArray.filter(pick => pick.overallScore !== null && pick.overallScore !== undefined);
        
        // Sort by score (highest/worst first)
        // If tied, sort by tier (descending, so higher tiers come first)
        const sortedPicks = picksWithScores.sort((a, b) => {
            if (a.overallScore !== b.overallScore) {
                return b.overallScore - a.overallScore; // Sort by score descending (worst first)
            }
            return b.tier - a.tier; // If tied, sort by tier descending (higher first)
        });
        
        // Take the first two (worst) picks
        const twoWorstPicks = sortedPicks.slice(0, 2);
        
        // Return the tiers of the two worst picks
        return twoWorstPicks.map(pick => pick.tier);
    };

    // Determine the two worst tiers for the current interval
    const worstTiers = useMemo(() => {
        if (!entry || !entry.intervalData) return [];
        
        // Find the relevant interval data
        const intervalData = entry.intervalData.find(data => 
            data.interval === parseInt(interval) || data.interval === interval
        );
        
        return getWorstTiersForInterval(intervalData);
    }, [entry, interval]);

    // Get intervals to display in descending order
    const intervalsToDisplay = useMemo(() => {
        if (interval !== 'Overall') {
            // If a specific interval is selected, just return that one
            return [interval];
        }
        
        // For Overall view, display intervals in descending order
        const intervals = [];
        const lastInterval = hasTheCurrentIntervalsDeadlinePassed ? theCurrentInterval : theCurrentInterval - 1;
        
        for (let i = lastInterval; i >= firstInterval; i--) {
            intervals.push(i);
        }
        
        return intervals;
    }, [interval, firstInterval, theCurrentInterval, hasTheCurrentIntervalsDeadlinePassed]);

    // Render players for a specific interval
    const renderIntervalPlayers = (intervalNum) => {
        if (!entry || !entry.intervalData) return null;
        
        // Find the relevant interval data
        const intervalData = entry.intervalData.find(data => 
            data.interval === parseInt(intervalNum) || data.interval === intervalNum
        );
        
        if (!intervalData || !intervalData.players) return null;
        
        // Get worst tiers for this specific interval
        const intervalWorstTiers = getWorstTiersForInterval(intervalData);
        
        // Sort players by tier
        const sortedPlayers = [...intervalData.players].sort((a, b) => a.tier - b.tier);
        
        return (
            <Box 
                key={`interval-${intervalNum}`}
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    margin: '20px auto',
                    alignItems: 'center', // Center the outer container horizontally
                }}
            >
                {interval === 'Overall' && (
                    <Typography
                        sx={{
                            fontSize: isMobile ? '11px' : '15px',
                            fontWeight: 700,
                            textAlign: 'center',
                            marginBottom: '8px',
                        }}
                    >
                        {intervalName(intervalNum)} {formatScore(intervalData?.intervalScore)}
                    </Typography>
                )}
                {interval !== 'Overall' && (
                    <Typography
                        sx={{
                            fontSize: isMobile ? '11px' : '15px',
                            fontWeight: 700,
                            textAlign: 'center',
                            marginBottom: '8px',
                        }}
                    >
                        {intervalName(intervalNum)}
                    </Typography>
                )}
                <Box
                    sx={{
                        width: 'fit-content', // Make the inner container only as wide as needed
                        minWidth: '60%',      // Ensure it has a reasonable minimum width
                        maxWidth: '80%',      // Limit maximum width
                    }}
                >
                    {sortedPlayers.map((player) => {
                        // Format the player score
                        let playerScore = null;
                        let statusText = '';
                        
                        // Determine status
                        if (player.cut) {
                            statusText = 'Cut';
                        } else if (player.withdrawn) {
                            statusText = 'WD';
                        }
                        
                        // Determine score
                        if (player.overallScore !== undefined && player.overallScore !== null) {
                            if (player.overallScore === 0) {
                                playerScore = '(E';
                            } else if (player.overallScore > 0) {
                                playerScore = `(+${player.overallScore}`;
                            } else {
                                playerScore = `(${player.overallScore}`;
                            }
                            
                            // Add status if exists and close parenthesis
                            if (statusText) {
                                playerScore += ` ${statusText})`;
                            } else {
                                playerScore += ')';
                            }
                        } else if (statusText) {
                            // If no score but has status
                            playerScore = `(${statusText})`;
                        }
                        
                        // Check if this tier is one of the worst two for this interval
                        const isWorstTier = intervalWorstTiers.includes(player.tier);
                        
                        return (
                            <Box
                                key={`tier-${intervalNum}-${player.tier}`}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    width: '100%',
                                    marginBottom: '8px',
                                }}
                            >
                                <Typography
                                    component="div"
                                    sx={{
                                        fontSize: isMobile ? '10px' : '14px',
                                        color: isWorstTier ? '#CC293C' : '#002129',
                                        fontWeight: '700',
                                        textAlign: 'left',
                                        width: '100%',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <Box component="span">Tier {player.tier}: </Box>
                                    <Box 
                                        component="span" 
                                        sx={{ 
                                            display: 'inline-block',
                                            maxWidth: isMobile ? playerScore ? '100px' : '140px' : '240px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            verticalAlign: 'bottom',
                                        }}
                                    >
                                        {getPlayerName(player.id)}
                                    </Box>
                                    {playerScore && (
                                        <Box component="span"> {playerScore}</Box>
                                    )}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        );
    };

    // Render all intervals
    const renderTierTable = () => {
        if (!entry || !entry.intervalData) return null;
        
        return (
            <Box sx={{ width: '100%' }}>
                {intervalsToDisplay?.map(intervalNum => renderIntervalPlayers(intervalNum))}
            </Box>
        );
    };

    return (
        <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        >
            <Box sx={style}>
                {/* Fixed header */}
                <Box sx={{ position: 'sticky', top: 0, bgcolor: 'background.paper', pb: 1, zIndex: 1 }}>
                    <Typography
                        sx={{
                            fontSize: isMobile ? '12px' : '16px',
                            fontWeight: 700,
                            textAlign: 'center',
                            marginBottom: '12px',
                        }}
                    >
                        {entry?.username} {findScore(entry, interval)}
                        <br /> Rank: {findRank(entry, interval)}
                    </Typography>
                </Box>

                {/* Scrollable content area */}
                <Box 
                    sx={{ 
                        maxHeight: '40vh', 
                        overflowY: 'auto',
                        // Add padding to prevent content from being cut off by the button
                        pb: 2
                    }}
                >
                    {renderTierTable()}
                </Box>

                {/* Fixed button at the bottom */}
                <Box sx={{ 
                    position: 'sticky', 
                    bottom: 0, 
                    bgcolor: 'background.paper', 
                    pt: 2,
                    display: 'flex', 
                    justifyContent: 'center',
                    zIndex: 1
                }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={onClose}
                        sx={{
                            color: '#fff',
                            '&:hover': {
                                color: '#fff',
                            }
                        }}
                    >
                        Close
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default TiersUserSelectionModal;